import { actions } from '@/services/actions';
import { computed, type ComputedRef, ref, type Ref, watch } from 'vue';
import { facilitiesViewingFromRoute, facilityIDFromRouteOrUserDefault } from '@/router';
import { delayedDetachedEffectScope, useArrayAsMap } from '@/services/utility';
import type { StandardTaskID } from '../../cross-app-shared/types/standard-tasks.ts';
import type { TaskGenerator } from '../../compliance-shared/types/compliance';
import type { StandardTaskBrief } from '../../compliance-shared/types/standard-tasks';
import { initialLoadingPromise } from '@/loading';

function waitForAuth<T>(defaultValue: T, init: () => Ref<T> | ComputedRef<T>): Ref<T> {
  const result = ref(defaultValue) as Ref<T>;

  initialLoadingPromise.then(() => {
    watch(
      init(),
      (newVal) => {
        result.value = newVal;
      },
      { immediate: true },
    );
  });

  return result;
}

export const suggestedTaskCount = waitForAuth(0, () =>
  actions.externalTask.listenForSuggestedTaskCount(facilitiesViewingFromRoute),
);

const facilityRoles = waitForAuth(null, () => actions.role.listenForFacilityRoles(facilityIDFromRouteOrUserDefault));
export const facilityMissingRoles = computed((): { environmentalManagers: boolean; generalManagers: boolean } => {
  if (facilityRoles.value === null) {
    return { environmentalManagers: false, generalManagers: false };
  }

  let environmentalManagers = true;
  let generalManagers = true;

  for (const role of facilityRoles.value) {
    if (role.type === 'environmentalManagers') {
      environmentalManagers = false;
    } else if (role.type === 'generalManagers') {
      generalManagers = false;
    }
  }

  return { environmentalManagers, generalManagers };
});

export const facilityRolesForCurrentRoute = delayedDetachedEffectScope(() =>
  actions.role.listenForMultipleFacilityRoles(facilitiesViewingFromRoute),
);

export const facilityGeneratorsForCurrentRoute = delayedDetachedEffectScope(
  (): { documents: Ref<Partial<Record<TaskGenerator['taskGeneratorID'], TaskGenerator>>>; isLoading: Ref<boolean> } => {
    const { documents, isLoading } =
      actions.generator.listenForMultipleFacilityActiveGenerators(facilitiesViewingFromRoute);
    return {
      documents: useArrayAsMap<TaskGenerator, TaskGenerator['taskGeneratorID']>(documents, 'taskGeneratorID'),
      isLoading,
    };
  },
);

export const myActiveGeneratorsById = delayedDetachedEffectScope(() => {
  return actions.generator.useMyActiveGeneratorsById();
});

export const standardTaskAdding = ref(null as null | StandardTaskID);

export const standardTasks = delayedDetachedEffectScope(() => {
  const list = actions.standardTask.listen();

  return computed((): StandardTaskBrief[] => {
    const newID = standardTaskAdding.value;
    if (newID) {
      const existing = list.value.find((t) => t.id === newID);
      if (existing) {
        return list.value;
      }

      return list.value.concat([{ id: newID, title: 'Title will be populated once this template saves' }]);
    }

    return list.value;
  });
});
